import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Building2, Recycle } from "lucide-react";
import { CATEGORIES, CATEGORY_MAP } from "@/lib/data";

const Block = ({ eyebrow, title, items, icon: Icon, testId }) => (
  <section data-testid={testId}>
    <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a] mb-2">{eyebrow}</p>
    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05130e] mb-8">{title}</h2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((it, i) => (
        <article key={it.t} className="rise bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg hover:border-[#00f2a1] transition-[transform,box-shadow,border-color] duration-300"
          style={{ animationDelay: `${i * 70}ms` }} data-testid={`${testId}-item-${i}`}>
          <span className="w-11 h-11 rounded-xl bg-[#0a1f18] flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-[#00f2a1]" /></span>
          <h3 className="font-semibold text-lg text-[#05130e] leading-snug">{it.t}</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{it.d}</p>
        </article>
      ))}
    </div>
  </section>
);

export default function CategoryPage() {
  const { id } = useParams();
  const c = CATEGORY_MAP[id];
  if (!c) return <Navigate to="/" replace />;
  const others = CATEGORIES.filter((o) => o.id !== id);

  return (
    <main data-testid="category-page">
      <section className="relative overflow-hidden bg-[#0a1f18] grain">
        <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f18] via-[#0a1f18]/85 to-[#0a1f18]/40" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="category-back-link"><ArrowLeft className="w-4 h-4" /> Categorías</Link>
          <c.icon className="w-10 h-10 text-[#00f2a1] mb-4" />
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white max-w-3xl leading-tight" data-testid="category-title">{c.name}</h1>
          <p className="mt-4 text-base md:text-lg text-slate-300">{c.items}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to={`/marketplace?category=${c.id}`} data-testid="category-view-lots-btn" className="inline-flex h-12 items-center justify-center gap-2 px-7 rounded-full bg-[#00f2a1] text-[#05130e] font-semibold hover:bg-[#00d890] hover:shadow-[0_0_22px_rgba(0,242,161,0.45)] transition-[background-color,box-shadow] duration-300">
              Ver lotes disponibles <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/publicar" data-testid="category-publish-btn" className="inline-flex h-12 items-center justify-center px-7 rounded-full border border-[#00f2a1]/40 text-white hover:bg-[#00f2a1]/10 transition-colors">
              Publicar un lote de esta categoría
            </Link>
          </div>
        </div>
      </section>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 space-y-20">
        <Block eyebrow="Industrias demandantes" title="¿Quién compra este material?" items={c.industries} icon={Building2} testId="category-industries" />
        <Block eyebrow="Valorización" title="Alternativas de uso y procesos de valorización" items={c.uses} icon={Recycle} testId="category-uses" />
        <section>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Otras categorías</p>
          <div className="flex flex-wrap gap-3">
            {others.map((o) => (
              <Link key={o.id} to={`/categorias/${o.id}`} data-testid={`category-other-${o.id}`} className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-sm text-slate-700 hover:border-[#00c487] hover:text-[#05130e] transition-colors">
                <o.icon className="w-4 h-4 text-[#00b37a]" />{o.short}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
